import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Wallet, TrendingUp, Bell, Shield, Smartphone, Download, ExternalLink, Check } from 'lucide-react';
import { appFeatures, companyInfo } from '../../data/mock';
import { toast } from 'sonner';

const iconMap = {
  Wallet,
  TrendingUp,
  Bell,
  Shield
};

const MobileApp = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if app is already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    // Listen for the beforeinstallprompt event
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        toast.success('Application installée avec succès !');
        setIsInstalled(true);
      }
      setDeferredPrompt(null);
    } else {
      // Fallback: open the app in a new tab
      window.open('/app/login', '_blank');
    }
  };

  const handleOpenApp = () => {
    window.location.href = '/app/login';
  };

  return (
    <section id="app" className="py-24 bg-gradient-to-br from-[#6B2C91]/5 via-white to-[#FFD700]/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left - Phone Mockup */}
          <div className="relative order-2 lg:order-1">
            <div className="relative max-w-sm mx-auto">
              {/* Decorative elements */}
              <div className="absolute -top-10 -left-10 w-40 h-40 bg-[#6B2C91]/10 rounded-full blur-3xl" />
              <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-[#FFD700]/10 rounded-full blur-3xl" />

              {/* Phone */}
              <div className="relative bg-gray-900 rounded-[3rem] p-3 shadow-2xl">
                {/* Notch */}
                <div className="absolute top-6 left-1/2 -translate-x-1/2 w-24 h-6 bg-gray-900 rounded-full z-10" />
                
                {/* Screen */}
                <div className="bg-white rounded-[2.5rem] overflow-hidden">
                  {/* Status bar */}
                  <div className="h-12 bg-[#6B2C91] flex items-end justify-center pb-2">
                    <span className="text-white font-semibold text-sm">KIPAY</span>
                  </div>

                  {/* App content */}
                  <div className="p-6 space-y-4">
                    {/* Profile card */}
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-[#6B2C91]/10 rounded-full flex items-center justify-center">
                        <span className="text-[#6B2C91] font-bold">MK</span>
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">Marie Kouassi</div>
                        <div className="text-xs text-[#FFD700] font-medium">Membre Gold</div>
                      </div>
                    </div>

                    {/* Points card */}
                    <div className="bg-gradient-to-br from-[#6B2C91] to-[#5a2579] rounded-2xl p-4 text-white">
                      <div className="text-sm text-white/70 mb-1">Vos Points</div>
                      <div className="text-3xl font-bold">24,850</div>
                      <div className="flex items-center gap-1 text-sm text-[#00C853] mt-1">
                        <TrendingUp className="w-3 h-3" />
                        +1,250 ce mois
                      </div>
                    </div>

                    {/* Quick actions */}
                    <div className="grid grid-cols-3 gap-2">
                      <div className="bg-gray-50 rounded-xl p-3 text-center">
                        <Wallet className="w-5 h-5 mx-auto text-[#6B2C91] mb-1" />
                        <span className="text-xs text-gray-600">Payer</span>
                      </div>
                      <div className="bg-gray-50 rounded-xl p-3 text-center">
                        <TrendingUp className="w-5 h-5 mx-auto text-[#6B2C91] mb-1" />
                        <span className="text-xs text-gray-600">Historique</span>
                      </div>
                      <div className="bg-gray-50 rounded-xl p-3 text-center">
                        <Bell className="w-5 h-5 mx-auto text-[#6B2C91] mb-1" />
                        <span className="text-xs text-gray-600">Offres</span>
                      </div>
                    </div>

                    {/* Recent transaction */}
                    <div className="border-t pt-4">
                      <div className="text-xs text-gray-500 mb-2">Dernière transaction</div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-[#FFD700]/20 rounded-lg flex items-center justify-center">
                            <span className="text-xs font-bold text-[#FFD700]">W</span>
                          </div>
                          <div>
                            <div className="text-sm font-medium">Wayne Station</div>
                            <div className="text-xs text-gray-500">Aujourd'hui, 14:32</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-semibold">-25 000 F</div>
                          <div className="text-xs text-[#00C853]">+50 pts</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right - Content */}
          <div className="order-1 lg:order-2 space-y-8">
            <div>
              <span className="text-[#6B2C91] font-semibold text-sm uppercase tracking-wider">
                Application Mobile
              </span>
              <h2 className="mt-3 text-3xl sm:text-4xl font-bold text-gray-900">
                Tout Votre Écosystème dans Votre Poche
              </h2>
              <p className="mt-4 text-lg text-gray-600">
                L'app KIPAY, c'est bien plus qu'un simple wallet. C'est votre tableau de bord 
                personnel pour gérer vos points, vos paiements et vos avantages.
              </p>
            </div>

            {/* Features Grid */}
            <div className="grid sm:grid-cols-2 gap-4">
              {appFeatures.map((feature, index) => {
                const Icon = iconMap[feature.icon];
                return (
                  <Card key={index} className="p-4 border-0 shadow-md hover:shadow-lg transition-shadow bg-white">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-[#6B2C91]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-[#6B2C91]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{feature.title}</h3>
                        <p className="text-sm text-gray-600">{feature.description}</p>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>

            {/* Download buttons */}
            <div className="space-y-4">
              <div className="flex flex-wrap gap-4">
                <Button
                  size="lg"
                  onClick={handleOpenApp}
                  className="bg-[#6B2C91] hover:bg-[#5a2579] text-white px-8"
                >
                  <ExternalLink className="mr-2 w-5 h-5" />
                  Ouvrir l'Application
                </Button>
                <Button
                  size="lg"
                  onClick={handleInstallClick}
                  variant="outline"
                  className="border-[#6B2C91] text-[#6B2C91] hover:bg-[#6B2C91]/10 px-8"
                  disabled={isInstalled}
                >
                  {isInstalled ? (
                    <>
                      <Check className="mr-2 w-5 h-5" />
                      Installée
                    </>
                  ) : (
                    <>
                      <Download className="mr-2 w-5 h-5" />
                      Installer sur Android
                    </>
                  )}
                </Button>
              </div>
              
              {/* Google Play Badge */}
              <div className="flex items-center gap-4 pt-2">
                <a 
                  href="https://play.google.com/store" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-block"
                >
                  <div className="bg-black text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-gray-800 transition-colors">
                    <svg viewBox="0 0 24 24" className="w-6 h-6 fill-current">
                      <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z" />
                    </svg>
                    <div>
                      <div className="text-[10px] leading-tight">Télécharger sur</div>
                      <div className="text-sm font-semibold leading-tight">Google Play</div>
                    </div>
                  </div>
                </a>
                <span className="text-sm text-gray-500">Bientôt disponible</span>
              </div>
            </div>

            <p className="text-sm text-gray-500">
              100% Gratuite. Aucun Achat In-App. iOS 13+ ou Android 8+
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MobileApp;
